class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (typeof cartDrawerCheckTrainingRule === 'function') cartDrawerCheckTrainingRule();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDrawerTrainingUpsell extends HTMLElement {
  constructor() {
    super();
    this.addButton = this.querySelector('#CartDrawer-TrainingUpsell-Add');
    this.errorElement = this.querySelector('#CartDrawer-TrainingUpsell-Error');
    this.addButton.addEventListener('click', this.onAddClick.bind(this));
  }

  onAddClick(event) {
    event.preventDefault();
    if (this.addButton.hasAttribute('disabled')) return;

    this.addButton.setAttribute('disabled', 'disabled');
    this.addButton.classList.add('loading');
    this.errorElement.textContent = '';

    const body = JSON.stringify({
      items: [{ id: this.dataset.variantId, quantity: 1 }],
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.status) {
          this.errorElement.textContent = parsedState.description || parsedState.message;
          this.addButton.removeAttribute('disabled');
          this.addButton.classList.remove('loading');
          return;
        }

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) ||
            document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        });

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-training-upsell', cartData: parsedState });
      })
      .catch(() => {
        this.errorElement.textContent = window.cartStrings.error;
        this.addButton.removeAttribute('disabled');
        this.addButton.classList.remove('loading');
      });
  }

  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

customElements.define('cart-drawer-training-upsell', CartDrawerTrainingUpsell);

const CART_DRAWER_CORE_SYSTEM_HANDLE = 'the-soundself-core-system';
const CART_DRAWER_TRAINING_HANDLE = 'on-site-team-training';
const CART_DRAWER_TRAINING_REMOVED_MESSAGE =
  'On-Site Team Training is only available when purchased together with the SoundSelf Core System, so it was removed from your cart.';

function cartDrawerShowMessage(message) {
  const errorsElement = document.getElementById('CartDrawer-CartErrors');
  if (!errorsElement) return;
  errorsElement.textContent = message;
}

function cartDrawerReplaceSections(parsedState) {
  const sections = [
    { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
    { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
  ];

  sections.forEach((section) => {
    const target = document.getElementById(section.id);
    if (!target) return;
    const elementToReplace = target.querySelector(section.selector) || target;
    const html = new DOMParser().parseFromString(parsedState.sections[section.section], 'text/html').querySelector(
      section.selector
    );
    if (html) elementToReplace.innerHTML = html.innerHTML;
  });

  const isEmpty = parsedState.item_count === 0;
  const cartDrawerWrapper = document.querySelector('cart-drawer');
  const cartDrawerItems = document.querySelector('cart-drawer-items');
  if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', isEmpty);
  if (cartDrawerItems) cartDrawerItems.classList.toggle('is-empty', isEmpty);
}

function cartDrawerItemMatchesHandle(item, handle) {
  if (item.handle === handle) return true;
  if (typeof item.url === 'string' && item.url.indexOf(`/products/${handle}`) !== -1) return true;
  return false;
}

const CART_DRAWER_TRAINING_QTY_CAPPED_MESSAGE =
  'On-Site Team Training is limited to one per order, so the quantity was adjusted.';

function cartDrawerChangeTrainingQuantity(key, quantity, message) {
  const body = JSON.stringify({
    id: key,
    quantity: quantity,
    sections: ['cart-drawer', 'cart-icon-bubble'],
    sections_url: window.location.pathname,
  });

  fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
    .then((response) => response.json())
    .then((parsedState) => {
      cartDrawerReplaceSections(parsedState);
      cartDrawerShowMessage(message);
    })
    .catch((e) => console.error('[training-sync] change fetch failed', e));
}

function cartDrawerEnforceTrainingRule(cartData) {
  if (!cartData || !cartData.items) return;

  const hasCoreSystem = cartData.items.some((item) =>
    cartDrawerItemMatchesHandle(item, CART_DRAWER_CORE_SYSTEM_HANDLE)
  );
  const trainingItem = cartData.items.find((item) =>
    cartDrawerItemMatchesHandle(item, CART_DRAWER_TRAINING_HANDLE)
  );

  if (!trainingItem) return;

  if (!hasCoreSystem) {
    cartDrawerChangeTrainingQuantity(trainingItem.key, 0, CART_DRAWER_TRAINING_REMOVED_MESSAGE);
    return;
  }

  if (trainingItem.quantity > 1) {
    cartDrawerChangeTrainingQuantity(trainingItem.key, 1, CART_DRAWER_TRAINING_QTY_CAPPED_MESSAGE);
  }
}

function cartDrawerCheckTrainingRule() {
  fetch(`${routes.cart_url}.js`)
    .then((response) => response.json())
    .then((cartData) => cartDrawerEnforceTrainingRule(cartData))
    .catch((e) => console.error('[training-sync] cart fetch failed', e));
}

subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
  cartDrawerCheckTrainingRule();
});
